import { useEffect, useMemo, useRef } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SFormikTextfield } from '@setvi/shared/components';
import { useCustomErrors } from '@setvi/shared/hooks';

import {
  InputContainer,
  FormikContainer,
  Placeholder,
  inputStyle
} from '../styled';
import { SearchFormProps } from '..';
import { ClearButton } from '../components';
import { SearchFormOptions } from '../../../enums';
import { ProductMatchValues } from '../../..';

export const formId = 'product-match-form';

export enum VALIDATORS {
  TEXT = 2048
}

export const textValidation = Yup.object({
  [SearchFormOptions.text]: Yup.string().max(
    VALIDATORS.TEXT,
    `Text must be at most ${VALIDATORS.TEXT} characters.`
  )
});

export const schema = [
  {
    name: ProductMatchValues.Text,
    schema: textValidation
  }
];

const MIN_ROWS = 4;
interface ProductTextInputProps extends Omit<
  SearchFormProps,
  'disableButton' | 'webUrlRows' | 'imageTextRows'
> {
  rows?: number;
  handleInputValidation: (invalid: boolean) => void;
}

const ProductTextInput = ({
  rows = MIN_ROWS,
  values,
  onSubmit,
  onChange,
  handleInputValidation
}: ProductTextInputProps) => {
  const ref = useRef(null);

  const { customErrors, customValidate } = useCustomErrors({
    schemas: schema,
    values: { [ProductMatchValues.Text]: values?.[ProductMatchValues.Text] }
  });

  const initialValues = useMemo(
    () => ({
      [ProductMatchValues.Text]: values?.[ProductMatchValues.Text]
    }),
    [values]
  );
  // @ts-ignore
  const isValid = !customErrors?.text && !!values?.[ProductMatchValues.Text];

  useEffect(() => {
    handleInputValidation?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customErrors]);

  const handleFocus = async () => {
    const textarea = ref?.current?.querySelector('textarea');
    if (textarea.value !== values?.[ProductMatchValues.Text]) {
      textarea.value = values?.[ProductMatchValues.Text];
    }

    await textarea?.focus();
    // Move cursor to the end
    const length = textarea?.value?.length || 0;
    textarea?.setSelectionRange(length, length);
  };

  useEffect(() => {
    handleFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormikContainer>
      <Formik
        id={formId}
        onSubmit={() =>
          isValid &&
          onSubmit?.(SearchFormOptions.text, {
            value: values?.[ProductMatchValues.Text],
            [ProductMatchValues.Text]: values?.[ProductMatchValues.Text]
          })
        }
        validateOnChange={false}
        enableReinitialize
        initialValues={initialValues}>
        {({ values: formikValues, resetForm, submitForm }) => (
          <Form id={formId}>
            <InputContainer
              // @ts-ignore
              ref={ref}>
              {!formikValues?.text && (
                <Placeholder>
                  <p>Example:</p>
                  <p>
                    Zep Neutral pH Floor Cleaner cleans and restores shine to
                    floor surfaces. Utilize this floor cleaner in Step 4 of the
                    Zep 4-Step Pro Floor Maintenance System. Concentrate works
                    great on marble, granite, vinyl and stone floors and can be
                    applied using an automatic scrubber or a mop and bucket.
                  </p>
                </Placeholder>
              )}
              <SFormikTextfield
                name={ProductMatchValues.Text}
                placeholder=""
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitForm();
                  }
                }}
                maxLength={VALIDATORS.TEXT}
                onChange={(value: string) => {
                  customValidate({
                    schema: textValidation,
                    value,
                    name: ProductMatchValues.Text
                  });
                  onChange({ text: value });
                }}
                multiline
                minRows={rows}
                maxRows={rows}
                InputProps={{
                  style: inputStyle,
                  endAdornment: !!formikValues?.text && (
                    <ClearButton
                      onClear={() => {
                        resetForm();
                        onChange({ text: '' });
                      }}
                    />
                  )
                }}
                customErrors={customErrors}
              />
            </InputContainer>
          </Form>
        )}
      </Formik>
    </FormikContainer>
  );
};

export default ProductTextInput;
